import moment from 'utils/app-moment';
import { dashIfEmpty } from '../../utils/empty';
import { Statuses } from './Statuses';
import TicketTypeHelper from './TicketTypeHelper';
import {
  EachWorkdeskTicketI,
  SubmissionResultResI,
} from './workdesk-tickets/each-workdesk-ticket.interface';

const formatTime = (time): string | null => {
  return time
    ? `${moment(time).format(
        'YYYY-MM-DD HH:mm', // HH is 24 hrs, hh is 12 hrs (will have to add A to show am pm)
      )}`
    : null;
};

class EachWorkdeskTicket {
  ticket: EachWorkdeskTicketI;
  constructor(ticket: EachWorkdeskTicketI) {
    this.ticket = this.parseSubmissionResults(ticket);
  }

  parseSubmissionResults(ticket) {
    if (!ticket) return ticket;
    let submissionResults = undefined as
      | undefined
      | SubmissionResultResI;
    if (!ticket.submissionResults) {
      submissionResults = undefined;
    } else {
      if (typeof ticket.submissionResults === 'string') {
        submissionResults = JSON.parse(ticket.submissionResults);
        if (!Array.isArray(submissionResults)) {
          console.error(
            `${ticket.id} parsed submissionResults and didn't get array ${ticket.submissionResults}`,
          );
          //   reset to undefined
          submissionResults = undefined;
        }
      } else {
        submissionResults = ticket.submissionResults;
      }
    }
    return {
      ...ticket,
      submissionResults,
    };
  }

  isTicketFailedWithError() {
    const failed = this.getTicketFailedSubmissionResults();
    return this.ticket.status === 'FAILED' && failed.length > 0;
  }

  shouldShowHasSubmissionMessages(): boolean {
    if (!['FAILED', 'SUCCESS'].includes(this.ticket.status))
      return false;
    return this.ticketHasSubmissionMessages();
  }

  ticketHasSubmissionMessages(): boolean {
    const messages = this.getTicketSubmissionResults();
    return messages.length > 0;
  }

  getTicketFailedSubmissionResults() {
    if (!this.ticket.submissionResults) return [];
    return this.ticket.submissionResults.filter(
      (result) => result.status === 'F',
    );
  }

  getTicketSubmissionResults(): SubmissionResultResI {
    if (!this.ticket.submissionResults) return [];
    return this.ticket.submissionResults;
    // below is for test only TODO: remove
    // .concat({
    //   status: 'S',
    //   msg: 'Some success message',
    //   bookingNo: 'SINAXXXXX',
    // });
  }

  getFormattedDetail = () => {
    const ticket = this.ticket;
    const ticketType = TicketTypeHelper.getTicketTypeFromSi(
      ticket.si,
    );
    return {
      ...ticket,
      bookingsNosText:
        ticket.bookingNos &&
        `${(ticket.bookingNos || []).join(', ')}`,
      statusText:
        ticket.status &&
        Statuses.getLabelOfStatus(ticket.status, ticketType),
      receivedTime: formatTime(ticket.receivedTime),
      receivedInWebAgent: formatTime(ticket.creationTime),
      assignTime: formatTime(ticket.userClickTime),
      sentInitial: formatTime(ticket.submitBookingDateInitial),
      sentLatest: formatTime(ticket.submitBookingDate),
      updateDate: formatTime(ticket.updateDate),
      indexerId: ticket.senderId,
      inbox: ticket.inboxAddress,
      preprocessingDoneTime: formatTime(ticket.preprocessingDoneTime),
    };
  };

  /**
   * TODO: put this into the WorkdeskTickets model
   * @param ticket
   */
  getDetailTicket = () => {
    const formatted = this.getFormattedDetail();
    const data: {
      title: string;
      detail: string;
      copyAble: boolean;
      hoverText?: string;
    }[] = [
      {
        title: 'Booking No.:',
        detail: formatted.bookingsNosText,
      },
      {
        title: 'Ticket ID:',
        detail: formatted.id,
      },
      {
        title: 'Status:',
        detail: formatted.statusText,
      },
      { title: 'SI Kind:', detail: formatted.siKind },
      {
        title: 'Inbox:',
        detail: formatted.inbox,
      },
      { title: 'Country:', detail: formatted.country },
      { title: 'Indexer ID:', detail: formatted.senderId },
      {
        title: 'Received Time:',
        detail: formatted.receivedTime,
      },
      {
        title: 'Received In Web Agent:',
        detail: formatted.receivedInWebAgent,
      },
      {
        title: 'Preprocess Turntime:',
        detail: formatted.preprocessTurntime,
      },
      {
        title: 'Assign Time:',
        detail: formatted.assignTime,
      },
      {
        title: 'Sent (Initial):',
        detail: formatted.sentInitial,
      },
      {
        title: 'Sent (Latest):',
        detail: formatted.sentLatest,
      },
      {
        title: 'Indexer Turntime:',
        detail: formatted.indexerTurnTime,
        hoverText: `from assign to sent (initial)`,
      },
      {
        title: 'Overall Turntime:',
        detail: formatted.overallTurnTime,
      },

      {
        title: 'Update Date:',
        detail: formatted.updateDate,
      },
      { title: 'Team code:', detail: formatted.teamCode },
    ].map((d) => {
      let final = {
        ...d,
        detail: dashIfEmpty(d.detail),
        hoverText: d.hoverText,
      };
      final['copyAble'] =
        final.detail && !['waiting...', '-'].includes(final.detail);
      return final as {
        title: string;
        detail: string;
        copyAble: boolean;
        hoverText?: string;
      };
    });
    console.debug('getDetailTicket:', data);
    return data;
  };
}

export default EachWorkdeskTicket;
